
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';
import { getTenant, getAppName, setRedirecting } from './tenant.util';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  console.log('[AuthGuard] Running auth guard for:', window.location.href);

  // STEP 1: Resolve the tenant/backend.
  //
  // Tenant identity is stored PER-TAB in sessionStorage (NOT localStorage) so tabs for
  // different tenants stay isolated. A valid tenant in the URL path (the MAG → SM entry)
  // is AUTHORITATIVE and overwrites the tab's stored AppName; internal SPA navigations
  // (e.g. router.navigateByUrl('/supplier-home')) carry no tenant segment because
  // <base href> is the static "/obmui/", so they resolve from the tab's stored AppName.
  //   getTenant() priority: (1) valid tenant in URL (overwrites)  (2) stored appName
  //                         (3) stored tenant    (4) null  (QA has no 'default')
  const backendUrl = getTenant();
  console.log('[AuthGuard] Resolved appName:', getAppName(), '| Backend URL:', backendUrl);

  // STEP 2: No AppName resolvable → user has not come through MAG (or the stored
  // AppName is unknown/stale). Send them to MAG login to authenticate.
  if (!backendUrl) {
    const magLoginUrl = (environment as any).magLoginUrl;
    console.log('[AuthGuard] No AppName in sessionStorage/URL - redirecting to MAG portal:', magLoginUrl);

    if (magLoginUrl) {
      setRedirecting(true);
      window.location.href = magLoginUrl;
    }
    return false;
  }

  // STEP 3: Check if user has token
  let token = authService.getToken();

  if (!token) {
    const appName = (getAppName() || '').toLowerCase();
    const mode = (environment as any).tenantAuthMode?.[appName] ?? 'wsfed';

    // Non-cutover tenants run their backend in MAGBypass mode (fake identity from
    // Azure config). Use a NON-credentialed check and NEVER redirect to MAG login;
    // if the backend isn't reachable, route to the error page.
    if (mode === 'magbypass') {
      const isBypass = await authService.checkMagBypass();   // non-credentialed; sets 'MAG_BYPASS_ENABLED'
      if (isBypass) {
        console.log('[AuthGuard] MAGBypass active for tenant:', appName);
        setRedirecting(false);
        return true;
      }
      console.warn('[AuthGuard] MAGBypass backend not reachable for tenant:', appName);
      return router.parseUrl('/server-error');
    }

    // ===== WS-Federation (cutover, e.g. sem1) — DO NOT MODIFY — working flow =====
    // Check if user is already authenticated on backend (via WS-Fed cookie)
    const isAuthenticated = await authService.checkBackendAuth();
    
    if (isAuthenticated) {
      console.log('[AuthGuard] User authenticated via backend (WS-Fed/MAGBypass)');
      setRedirecting(false);
      return true;
    }

    // Not authenticated - redirect to backend for WS-Federation login
    console.log('[AuthGuard] Not authenticated - redirecting to backend for WS-Fed login');
    const backendBaseUrl = backendUrl.replace('/API', '');
    const returnUrl = encodeURIComponent(window.location.href);
    
    // Redirect to backend login endpoint - this will trigger WS-Fed → MAG login
    const loginUrl = `${backendBaseUrl}/auth/startlogin?returnUrl=${returnUrl}`;
    console.log('[AuthGuard] Redirecting to:', loginUrl);
    setRedirecting(true);
    window.location.href = loginUrl;
    return false;
  }

  console.log('[AuthGuard] User has token, allowing access');
  // Defensive: clear any stale redirect flag so a persisted sessionStorage
  // 'sm_redirecting' from a prior MAG round-trip can never leave the
  // "Redirecting to login..." overlay showing once the user is admitted.
  setRedirecting(false);
  return true;
};
